// File: src/components/admin/AuditLogView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    MenuItem,
    Grid,
    CircularProgress,
    Stack,
    Alert
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    GetApp as ExportIcon,
    CloudDownload as DownloadIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    Schedule as TimeIcon,
    Person as UserIcon
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../services/adminApi';
import { downloadAsJson } from '../../utils/downloadUtils';

const AuditLogView = ({ defaultFilters = {} }) => {
    const navigate = useNavigate();
    // Audit State
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pageCursor, setPageCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);

    // Filters
    const [filterUid, setFilterUid] = useState(defaultFilters.uid || '');
    const [filterAction, setFilterAction] = useState(defaultFilters.action || 'ALL');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });

    const fetchLogs = useCallback(async (cursor = null) => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                limit: 50,
                cursor: cursor || undefined,
            };
            if (filterUid) params.uid = filterUid;
            if (filterAction !== 'ALL') params.action = filterAction;
            if (dateRange.start) params.startDate = dateRange.start;
            if (dateRange.end) params.endDate = dateRange.end;

            const data = await adminApi.getAuditLogs(params);
            const logsArray = data?.items || [];

            // Append if cursor exists (load more), replace otherwise
            if (cursor) {
                setLogs(prev => [...prev, ...logsArray]);
            } else {
                setLogs(logsArray);
            }

            setPageCursor(data?.nextCursor || null);
            setHasMore(!!data?.nextCursor);
        } catch (err) {
            setError(handleAdminApiError(err).message);
        } finally {
            setLoading(false);
        }
    }, [filterUid, filterAction, dateRange.start, dateRange.end]);

    // Initial Load
    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleSearch = () => {
        setPageCursor(null);
        fetchLogs(null);
    };

    const handleLoadMore = () => {
        if (pageCursor) {
            fetchLogs(pageCursor);
        }
    };

    const handleExport = async () => {
        try {
            const params = {};
            if (filterUid) params.uid = filterUid;
            if (filterAction !== 'ALL') params.action = filterAction;
            if (dateRange.start) params.startDate = dateRange.start;
            if (dateRange.end) params.endDate = dateRange.end;

            const blob = await adminApi.exportAuditLogs(params);

            // Create download link
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `audit_logs_${new Date().toISOString()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            setError('Failed to export logs: ' + err.message);
        }
    };

    const getStatusColor = (status) => {
        if (!status) return 'default';
        if (status === 'success') return 'success';
        if (status === 'failure' || status === 'error') return 'error';
        return 'warning';
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1600, margin: '0 auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="bold">
                    Audit Logs
                </Typography>
                <Stack direction="row" spacing={2}>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={handleSearch}
                        variant="outlined"
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    <Button
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                            const metadata = {
                                filters: {
                                    ...(filterUid && { uid: filterUid }),
                                    ...(filterAction !== 'ALL' && { action: filterAction }),
                                    ...(dateRange.start && { startDate: dateRange.start }),
                                    ...(dateRange.end && { endDate: dateRange.end }),
                                }
                            };
                            downloadAsJson(logs, 'audit-logs', metadata);
                        }}
                        variant="outlined"
                        disabled={logs.length === 0}
                        data-testid="audit-logs-list-btn-download"
                    >
                        Download JSON
                    </Button>
                    <Button
                        startIcon={<ExportIcon />}
                        onClick={handleExport}
                        variant="contained"
                        color="primary"
                    >
                        Export CSV
                    </Button>
                </Stack>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="User ID"
                            size="small"
                            value={filterUid}
                            onChange={(e) => setFilterUid(e.target.value)}
                            disabled={!!defaultFilters.uid} // Disable if fixed via props
                            InputProps={{
                                startAdornment: <UserIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            select
                            label="Action Type"
                            size="small"
                            value={filterAction}
                            onChange={(e) => setFilterAction(e.target.value)}
                        >
                            <MenuItem value="ALL">All Actions</MenuItem>
                            <MenuItem value="ai_generate_text">AI Generate Text</MenuItem>
                            <MenuItem value="ai_generate_image">AI Generate Image</MenuItem>
                            <MenuItem value="scheduler_ai_analysis">Scheduler: AI Analysis</MenuItem>
                            <MenuItem value="scheduler_stats_refresh">Scheduler: Stats Refresh</MenuItem>
                            <MenuItem value="subscription_granted">Subscription Granted</MenuItem>
                            <MenuItem value="subscription_revoked">Subscription Revoked</MenuItem>
                            <MenuItem value="user_login">User Login</MenuItem>
                            <MenuItem value="delete_account">Delete Account</MenuItem>
                            <MenuItem value="frontend_error">Frontend Error</MenuItem>
                            <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem', mt: 1 }}>— Autopilot —</MenuItem>
                            <MenuItem value="autopilot_activated">Autopilot Activated</MenuItem>
                            <MenuItem value="autopilot_paused">Autopilot Paused</MenuItem>
                            <MenuItem value="autopilot_resumed">Autopilot Resumed</MenuItem>
                            <MenuItem value="autopilot_config_updated">Autopilot Config Updated</MenuItem>
                            <MenuItem value="autopilot_recommendation_applied">Recommendation Applied</MenuItem>
                            <MenuItem value="autopilot_recommendation_dismissed">Recommendation Dismissed</MenuItem>
                            <MenuItem value="autopilot_cycle_completed">Cycle Completed</MenuItem>
                            <MenuItem value="autopilot_entity_mutated">Entity Mutated</MenuItem>
                            <MenuItem value="autopilot_auto_revert">Auto Revert</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                type="date"
                                label="Start Date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={dateRange.start}
                                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                            />
                            <Typography>-</Typography>
                            <TextField
                                type="date"
                                label="End Date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={dateRange.end}
                                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Logs Table */}
            <TableContainer component={Paper} elevation={2}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Details</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">No logs found matching criteria</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            logs.map((log) => (
                                <TableRow
                                    key={log.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/audit/${log.id}`)}
                                >
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <TimeIcon fontSize="small" color="action" />
                                            <Typography variant="body2" fontFamily="monospace">
                                                {new Date(log.timestamp).toLocaleString()}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Box onClick={(e) => e.stopPropagation()}> {/* Stop row click propagation */}
                                            <Typography
                                                variant="body2"
                                                component="a"
                                                href={`/users/${log.uid}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate(`/users/${log.uid}`);
                                                }}
                                                sx={{
                                                    color: 'primary.main',
                                                    textDecoration: 'none',
                                                    '&:hover': { textDecoration: 'underline' },
                                                    fontWeight: 500
                                                }}
                                            >
                                                {log.email || 'No Email'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200 }} noWrap title={log.uid}>
                                                {log.uid}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.action}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontWeight: 'medium' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {log.meta?.duration_ms ? `${log.meta.duration_ms}ms` : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={(log.meta?.status || 'success').toUpperCase()}
                                            size="small"
                                            color={getStatusColor(log.meta?.status || 'success')}
                                            sx={{ fontSize: '0.70rem', height: 24 }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/audit/${log.id}`);
                                        }}>
                                            <ViewIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Button
                        disabled={loading}
                        onClick={handleLoadMore}
                        variant="text"
                    >
                        {loading ? 'Loading...' : 'Load More Logs'}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default AuditLogView;
